import { Close, Luggage, ModeEdit, Person } from '@mui/icons-material'
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  LinearProgress,
  ListItem,
  ListItemText,
  Modal,
  Slide,
  Typography,
} from '@mui/material'
import Collapse from '@mui/material/Collapse'
import { grey } from '@mui/material/colors'
import { useMutation, useQuery } from '@tanstack/react-query'
import { bookingsAPI } from 'api/bookingsAPI'
import { clientsAPI } from 'api/clientsAPI'
import { TaxiQuote } from 'api/quotes/quoteTypes'
import { quotesAPI } from 'api/quotesAPI'
import { usersAPI } from 'api/usersAPI'
import DirectionsMap2 from 'components/Common/DirectionsMap/DirectionsMap2'
import DisplayPrice from 'components/Common/DisplayValues/DisplayPrice'
import notifyPopup from 'components/Common/Notification/notifyPopup'
import VehicleDisplayName from 'components/Common/VehicleDisplayName/VehicleDisplayName'
import useUserRole from 'hooks/useUserRole'
import * as _ from 'lodash'
import { fireBookingAmended } from 'analytics/bookingEvents'
import {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { confirmAlert } from 'react-confirm-alert'
import BookingInfo from './BookingInfo'
import BookingRefsForm from './BookingRefsForm'
import Header from './Header'
import InstructionsForm from './InstructionsForm'
import JourneyDetailsForm from './JourneyDetailsForm'
import PassengerForm from './PassengersForm'
import TravelDateForm from './TravelDateForm'
import VehicleJobs from './VehicleJobs'

const INITIAL_EDIT_STATES = {
  specialInstructions: false,
  flightDetails: false,
  general: false,
  date: false,
  journey: false,
  passengers: false,
  bookingRefs: false,
  vehicle: false,
  leadPassenger: false,
}

type EditStates = typeof INITIAL_EDIT_STATES

type ViewAmendModalProps = {
  openViewEditModal: boolean
  setOpenViewEditModal: (e: boolean) => void
  selectedBookingId: number
  setBookingId?: any
}

const QUOTE_PROGRESS_DURATION_MS = 6000

const QuoteProgressOverlay = ({ open }: { open: boolean }) => {
  const [quoteProgress, setQuoteProgress] = useState<number>(0)

  useEffect(() => {
    let interval: ReturnType<typeof setInterval> | null = null

    if (open) {
      setQuoteProgress(0)
      const startTime = Date.now()
      interval = setInterval(() => {
        const elapsed = Date.now() - startTime
        const nextValue = Math.min(
          (elapsed / QUOTE_PROGRESS_DURATION_MS) * 95,
          100,
        )
        setQuoteProgress(nextValue)

        if (nextValue >= 100 && interval) {
          clearInterval(interval)
          interval = null
        }
      }, 100)
    } else {
      setQuoteProgress(0)
    }

    return () => {
      if (interval) clearInterval(interval)
    }
  }, [open])

  if (!open) return null

  return (
    <Box
      sx={{
        position: 'absolute',
        inset: 0,
        backgroundColor: 'rgba(0,0,0,0.45)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: (theme) => theme.zIndex.modal + 1,
        backdropFilter: 'blur(2px)',
      }}
    >
      <Box
        sx={{
          width: { xs: '80%', sm: '60%', md: '40%' },
          maxWidth: 480,
          backgroundColor: '#fff',
          borderRadius: 2,
          boxShadow: 24,
          p: { xs: 3, sm: 4 },
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        <Typography variant="h6" textAlign="center">
          Checking available vehicles
        </Typography>
        <Typography variant="body2" textAlign="center">
          Hang tight while we check updated prices. This usually takes a few
          seconds.
        </Typography>
        <LinearProgress
          variant="determinate"
          value={quoteProgress}
          sx={{ height: 8, borderRadius: 5 }}
        />
        <Typography variant="caption" textAlign="center">
          {`${Math.round(quoteProgress)}% complete`}
        </Typography>
      </Box>
    </Box>
  )
}

type EditableSectionProps = {
  title?: ReactNode
  isEditable?: boolean
  onEdit: () => void
  children: ReactNode
  flex?: boolean
  editing?: boolean
  showClose?: boolean
  showDirtyBanner?: boolean
  isQuoteRefetching: boolean
  isAnyEditing: boolean
  sx?: any
}

const EditableSection = ({
  title,
  isEditable = false,
  onEdit,
  children,
  flex,
  editing = false,
  showClose = true,
  showDirtyBanner = false,
  isQuoteRefetching,
  isAnyEditing,
  sx,
}: EditableSectionProps) => {
  console.log('editableSection', showDirtyBanner, title)
  const editActionButtonSx = {
    ...sx,
    display: !showClose && editing ? 'none' : null,
    backgroundColor: '#00000010',
    boxShadow: 'none',
    color: 'ButtonText',
    textTransform: 'none',
    margin: title ? '5px' : '0px 5px 5px 5px',
    minWidth: 0,
    p: 0.5,
    '&:hover': {
      backgroundColor: '#00000020',
      boxShadow: 'none',
      color: 'ButtonText',
    },
    '&:active': {
      background: 'none',
      backgroundColor: 'transparent',
      boxShadow: 'none',
      color: 'ButtonText',
    },
    '&:focus': {
      background: 'none',
      backgroundColor: 'transparent',
      boxShadow: 'none',
      color: 'ButtonText',
    },
    '&.Mui-disabled': {
      background: 'none',
      backgroundColor: 'transparent',
      boxShadow: 'none',
      color: 'ButtonText',
      opacity: 0.5,
    },
  }

  const isAnotherSectionBeingEdited = isAnyEditing && !editing

  const actionButton =
    isEditable && !isQuoteRefetching ? (
      <IconButton
        onClick={onEdit}
        disabled={isAnotherSectionBeingEdited}
        sx={editActionButtonSx}
      >
        {editing ? (
          showClose ? (
            <Close fontSize="small" />
          ) : (
            <></>
          )
        ) : (
          <ModeEdit fontSize="small" />
        )}
      </IconButton>
    ) : null

  const SectionWrapper = ({
    children: sectionChildren,
  }: {
    children: ReactNode
  }) => (
    <Grid
      item
      xs={flex ? 12 : undefined}
      sx={{
        border: 1,
        borderColor: grey[400],
        borderRadius: '5px',
        backgroundColor: '#f7f7f7',
        position: 'relative',
        transition: 'max-height 0.3s ease-in-out, opacity 0.3s ease-in-out',
      }}
    >
      <Collapse
        in={showDirtyBanner && !isQuoteRefetching}
        timeout={250}
        mountOnEnter
        unmountOnExit
      >
        <Box
          sx={{
            width: '100%',
            backgroundColor: '#d33300',
            textTransform: 'uppercase',
            fontSize: '10px',
            fontWeight: 'bold',
            color: 'white !important',
          }}
        >
          Pending Change
        </Box>
      </Collapse>

      <Box
        sx={{
          position: 'absolute',
          top: showDirtyBanner && !isQuoteRefetching ? 24 : 8,
          right: 8,
          zIndex: 1,
          transition: 'top 0.25s ease-in-out',
        }}
      >
        {actionButton}
      </Box>
      {sectionChildren}
    </Grid>
  )

  if (!title) {
    return (
      <SectionWrapper>
        <Box
          sx={{
            display: 'flex',

            flexDirection: 'column',
            paddingY: 1,
            paddingX: 3,
            pr: 7,
            flexGrow: 0,
          }}
        >
          <Box sx={{ display: 'flex', margin: 0 }}>{children}</Box>
        </Box>
      </SectionWrapper>
    )
  }

  return (
    <SectionWrapper>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          paddingY: 2,
          paddingX: 3,
          pr: 7,
          flexGrow: 0,
          height: '100%',
        }}
      >
        <Typography
          variant="h6"
          sx={{
            display: 'flex',
            justifyContent: 'flex-start',
            height: 'auto',
          }}
        >
          {title}
        </Typography>
        {children}
      </Box>
    </SectionWrapper>
  )
}

type MainSectionProps = {
  quoteRefetch: (payload?: TaxiQuote) => Promise<any> | void
  isQuoteRefetching: boolean
  editStates: EditStates
  preparedQuote: TaxiQuote | undefined
  toggleEdit: (key: keyof EditStates) => void
  dropOff: any
  pickup: any
  additionalStops: any[]
  setDropOff: (value: any) => void
  setPickup: (value: any) => void
  setAdditionalStops: (value: any[]) => void
  canAmend: (type: any) => boolean
  editMode: boolean
  pushEditedState: (key: string) => void
  currentBooking: any
  updatePreparedQuote: (key: keyof TaxiQuote, value: any) => void
  setEditState: (key: keyof EditStates, value: boolean) => void
  updatePreparedAmend: (key: any, value: any) => void
  chosenVehicle: any
  setChosenVehicle: (vehicle: any) => void
  quotedJobs: any
  numPassengers: number
  setNumPassengers: (value: number) => void
  clientUserData: any
  uniqueReasons: string[]
  editedStates: string[]
  removedEditedState: (key: string) => void
  preparedAmend: any
  updateStops: (index: number, stop: any) => void
  isAnyEditing: boolean
  validateAccess: (role: string) => boolean
}

const MainSection = ({
  quoteRefetch,
  isQuoteRefetching,
  editStates,
  preparedQuote,
  toggleEdit,
  dropOff,
  pickup,
  additionalStops,
  setDropOff,
  setPickup,
  setAdditionalStops,
  canAmend,
  editMode,
  pushEditedState,
  currentBooking,
  updatePreparedQuote,
  setEditState,
  updatePreparedAmend,
  chosenVehicle,
  setChosenVehicle,
  quotedJobs,
  numPassengers,
  setNumPassengers,
  clientUserData,
  uniqueReasons,
  editedStates,
  removedEditedState,
  preparedAmend,
  updateStops,
  isAnyEditing,
  validateAccess,
}: MainSectionProps) => {
  const [selectedVehicle, setSelectedVehicle] = useState<any>(null)

  const memoOrigin = useMemo(
    () => preparedQuote?.outbound?.pickup ?? null,
    [
      preparedQuote?.outbound?.pickup?.latitude,
      preparedQuote?.outbound?.pickup?.longitude,
    ],
  )
  const memoDestination = useMemo(
    () => preparedQuote?.outbound?.dropOff ?? null,
    [
      preparedQuote?.outbound?.dropOff?.latitude,
      preparedQuote?.outbound?.dropOff?.longitude,
    ],
  )
  const memoAdditionalStops = useMemo(
    () =>
      (preparedQuote?.outbound?.additionalStops ?? []).filter(
        (s: any) =>
          s.latitude !== 0 && s.longitude !== 0 && s.formattedAddress !== '',
      ),
    [
      ...(preparedQuote?.outbound?.additionalStops ?? []).map(
        (s: any) => `${s.latitude}:${s.longitude}:${s.formattedAddress}`,
      ),
    ],
  )

  const directionsMapOptions = useMemo(() => ({ mapTypeControl: true }), [])

  const directionsMapElement = useMemo(
    () => (
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <DirectionsMap2
          mapHeight={editStates.vehicle ? '15dvh' : '30dvh'}
          mapWidth="100%"
          borderRadius="10px"
          boxShadow="#2e2e2e47 0px 2px 5px"
          origin={memoOrigin}
          destination={memoDestination}
          additionalStops={memoAdditionalStops}
          mapOptions={directionsMapOptions}
        />
      </Box>
    ),
    [
      memoOrigin,
      memoDestination,
      memoAdditionalStops,
      directionsMapOptions,
      editStates.vehicle,
    ],
  )

  if (!preparedQuote) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexGrow: 1,
          height: '100%',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Grid
      id="final"
      container
      style={{
        width: '100%',
        textAlign: 'center',
        padding: '1.563rem',
        height: '100%',
        overflow: 'auto',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          gap: '10px',
          width: '100%',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            width: '50%',
            flexDirection: 'column',
            justifyContent: 'space-between',
            gap: '10px',
          }}
        >
          {directionsMapElement}

          <EditableSection
            isEditable={canAmend('Requotable') && editMode}
            onEdit={() => {
              toggleEdit('date')
            }}
            editing={editStates.date}
            showClose={true}
            showDirtyBanner={editedStates.includes('changedDate')}
            isQuoteRefetching={isQuoteRefetching}
            isAnyEditing={isAnyEditing}
          >
            <TravelDateForm
              editStates={editStates}
              currentBooking={currentBooking}
              preparedQuote={preparedQuote}
              quoteRefetch={quoteRefetch}
              updatePreparedQuote={updatePreparedQuote}
              setEditState={setEditState}
              onDateApplied={() => pushEditedState('changedDate')}
            />
          </EditableSection>
          <EditableSection
            isEditable={canAmend('Requotable') && editMode}
            onEdit={() => {
              toggleEdit('journey')
              setAdditionalStops(preparedQuote?.outbound?.additionalStops ?? [])
            }}
            showClose={true}
            showDirtyBanner={editedStates.includes('changedJourney')}
            editing={editStates.journey}
            isQuoteRefetching={isQuoteRefetching}
            isAnyEditing={isAnyEditing}
          >
            {preparedQuote && (
              <JourneyDetailsForm
                editStates={editStates}
                currentBooking={currentBooking}
                preparedQuote={preparedQuote}
                updatePreparedQuote={updatePreparedQuote}
                updatePreparedAmend={updatePreparedAmend}
                quoteRefetch={quoteRefetch}
                isQuoteRefetching={isQuoteRefetching}
                dropOff={dropOff}
                pickup={pickup}
                additionalStops={additionalStops}
                validStops={_.isEqual(memoAdditionalStops, additionalStops)}
                updateStops={updateStops}
                setDropOff={setDropOff}
                setPickup={setPickup}
                setAdditionalStops={setAdditionalStops}
                onJourneyApplied={() => pushEditedState('changedJourney')}
              />
            )}
          </EditableSection>

          <EditableSection
            title={
              currentBooking.jobs?.length > 1 ? (
                <>
                  Vehicles <Chip label={currentBooking.jobs.length} />
                </>
              ) : (
                <>Vehicle</>
              )
            }
            isEditable={canAmend('Requotable') && editMode}
            flex={true}
            onEdit={() => {
              if (!editStates.vehicle) {
                setChosenVehicle(undefined)
                quoteRefetch()
              } else {
                setChosenVehicle(undefined)
              }
              toggleEdit('vehicle')
            }}
            showClose={true}
            showDirtyBanner={editedStates.includes('changedVehicle')}
            editing={editStates.vehicle}
            isQuoteRefetching={isQuoteRefetching}
            isAnyEditing={isAnyEditing}
          >
            {chosenVehicle?.vehicleDetails ? (
              <Box sx={{ height: '100%' }}>
                <Grid sx={{ alignItems: 'center' }}>
                  <ListItem alignItems="center">
                    {chosenVehicle?.vehicleDetails?.icon ? (
                      <Box
                        component="img"
                        src={chosenVehicle.vehicleDetails.icon}
                        sx={{ width: '12%', marginLeft: '10px' }}
                      />
                    ) : null}
                    <ListItemText
                      sx={{ marginLeft: '30px' }}
                      primary={
                        <>
                          <Box
                            sx={{
                              display: 'flex',
                              gap: '4px',
                              alignItems: 'center',
                            }}
                          >
                            <Typography
                              variant="body1"
                              sx={{
                                fontWeight: 'bold',
                                textTransform: 'uppercase',
                                fontSize: '1.2rem',
                              }}
                            >
                              {chosenVehicle?.vehicleDetails
                                ? VehicleDisplayName({
                                    vehicleDetails:
                                      chosenVehicle.vehicleDetails,
                                  })
                                : 'Vehicle'}
                            </Typography>
                          </Box>
                        </>
                      }
                      secondary={
                        <>
                          <Box
                            sx={{
                              display: 'flex',
                              gap: '5px',
                              alignItems: 'center',
                              paddingBottom: '5px',
                            }}
                          >
                            {validateAccess('Jyrney_Admin') && (
                              <Typography>
                                {chosenVehicle?.supplierName ?? ''}
                              </Typography>
                            )}
                          </Box>
                          <Box sx={{ display: 'flex', gap: '10px' }}>
                            Est.
                            {chosenVehicle &&
                              DisplayPrice({
                                VATTotalPrice:
                                  currentBooking?.jobs[0].estimatedPrice ?? 0,
                                displayVATPrice:
                                  currentBooking?.booking.client.displayVAT,
                                strikeThrough: true,
                              })}
                            {chosenVehicle &&
                              DisplayPrice({
                                VATTotalPrice:
                                  chosenVehicle?.estimatedTotal ?? 0,
                                displayVATPrice:
                                  currentBooking?.booking.client.displayVAT,
                                fontWeight: '700',
                              })}
                            <Typography
                              variant="body2"
                              sx={{
                                display: 'flex',
                                gap: '4px',
                                alignItems: 'center',
                              }}
                            >
                              <Person />
                              {`${chosenVehicle?.vehicleDetails?.seats ?? ''}`}
                              <Luggage />
                              {`${
                                chosenVehicle?.vehicleDetails?.luggage ?? ''
                              }`}
                            </Typography>
                          </Box>
                        </>
                      }
                    />
                  </ListItem>
                </Grid>
              </Box>
            ) : (
              <VehicleJobs
                currentBooking={currentBooking}
                quotedJobs={quotedJobs}
                numPassengers={numPassengers}
                preparedQuote={preparedQuote}
                editStates={editStates}
                updatePreparedAmend={updatePreparedAmend}
                setEditState={setEditState}
                chosenVehicle={chosenVehicle}
                setChosenVehicle={setChosenVehicle}
                selectedVehicle={selectedVehicle}
                setSelectedVehicle={setSelectedVehicle}
              />
            )}
          </EditableSection>
        </Box>

        <Box
          sx={{
            width: '50%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-start',
            gap: '15px',
          }}
        >
          {(canAmend('Non_Requotable') || canAmend('Requotable')) &&
            !uniqueReasons.length && (
              <Alert variant="standard" color="info">
                <Box sx={{ display: 'flex', alignContent: 'flex-start' }}>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '8px',
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        gap: '3px',
                        flexDirection: 'column',
                        alignItems: 'flex-start',
                      }}
                    >
                      <Typography fontWeight={500}>
                        Making changes to your booking:
                      </Typography>
                      <Typography textAlign="justify">
                        {currentBooking.jobs[0].amendPolicy}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Alert>
            )}
          {uniqueReasons.map((reason, idx) => (
            <Alert key={idx} variant="standard" color="warning">
              <Box sx={{ display: 'flex', alignContent: 'flex-start' }}>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '10px',
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      gap: '3px',
                      flexDirection: 'column',
                      alignItems: 'flex-start',
                      textAlign: 'left',
                    }}
                  >
                    <Typography fontWeight={500}>{reason}</Typography>
                  </Box>
                </Box>
              </Box>
            </Alert>
          ))}
          <Grid
            item
            sx={{
              justifyContent: 'space-between',
              width: '100%',
              alignItems: 'center',
              border: 1,
              borderColor: grey[400],
              paddingX: '12px',
              paddingTop: '7px',
              paddingBottom: '7px',
              borderRadius: '5px',
              backgroundColor: '#f7f7f7',
            }}
          >
            <BookingInfo
              currentBooking={currentBooking}
              preparedQuote={preparedQuote}
            />
          </Grid>

          <EditableSection
            title={'Booking References'}
            isEditable={canAmend('Non_Requotable') && editMode}
            onEdit={() => {
              toggleEdit('bookingRefs')
            }}
            showDirtyBanner={
              editStates.bookingRefs &&
              !_.isEqual(
                preparedQuote?.bookingReferences,
                currentBooking.booking.bookingReferenceValues,
              )
            }
            editing={editStates.bookingRefs}
            showClose={false}
            isQuoteRefetching={isQuoteRefetching}
            isAnyEditing={isAnyEditing}
          >
            <BookingRefsForm
              currentBooking={currentBooking}
              editStates={editStates}
              preparedQuote={preparedQuote}
              updatePreparedQuote={updatePreparedQuote}
              updatePreparedAmend={updatePreparedAmend}
            />
            {editStates.bookingRefs && (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  gap: '10px',
                  mt: 2,
                }}
              >
                <Button
                  variant="outlined"
                  onClick={() => {
                    toggleEdit('bookingRefs')
                    removedEditedState('bookingReferences')
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  onClick={() => {
                    pushEditedState('bookingReferences')
                    setEditState('bookingRefs', false)
                  }}
                >
                  Apply Changes
                </Button>
              </Box>
            )}
          </EditableSection>

          <EditableSection
            onEdit={() => {
              toggleEdit('passengers')
            }}
            isEditable={canAmend('Non_Requotable') && editMode}
            showDirtyBanner={
              editStates.passengers &&
              (numPassengers !==
                currentBooking.booking.numberOfPassengers - 1 ||
                !_.isEqual(
                  preparedQuote?.passengers,
                  currentBooking.booking.passengers,
                ))
            }
            editing={editStates.passengers}
            showClose={false}
            isQuoteRefetching={isQuoteRefetching}
            isAnyEditing={isAnyEditing}
            sx={{ flexDirection: 'column' }}
          >
            <PassengerForm
              editStates={editStates}
              toggleEdit={toggleEdit}
              preparedQuote={preparedQuote}
              updatePreparedQuote={updatePreparedQuote}
              updatePreparedAmend={updatePreparedAmend}
              numPassengers={numPassengers}
              setNumPassengers={setNumPassengers}
              bookingStatus={currentBooking.booking.bookingStatusTypeName}
              clientUserData={clientUserData}
              maxPassengers={
                chosenVehicle?.vehicleDetails?.seats ??
                currentBooking?.booking?.totalBookingSeats ??
                currentBooking?.jobs?.[0]?.vehicleDetails?.seats ??
                0
              }
            />
            {editStates.passengers && (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  alignItems: 'flex-end',
                  gap: '10px',
                  mt: 2,
                }}
              >
                <Button
                  variant="outlined"
                  onClick={() => {
                    toggleEdit('passengers')
                    removedEditedState('passengers')
                    removedEditedState('numberOfPassengers')
                    console.log('editedStates', editedStates)
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  onClick={() => {
                    if (
                      numPassengers !==
                      currentBooking.booking.numberOfPassengers - 1
                    ) {
                      pushEditedState('numberOfPassengers')
                      updatePreparedAmend(
                        'numberOfPassengers',
                        numPassengers + 1,
                      )
                    }
                    if (
                      !_.isEqual(
                        preparedQuote?.passengers,
                        currentBooking.booking.passengers,
                      )
                    ) {
                      pushEditedState('passengers')
                      updatePreparedAmend(
                        'passengers',
                        preparedQuote?.passengers,
                      )
                    }
                    setEditState('passengers', false)
                  }}
                >
                  Apply Changes
                </Button>
              </Box>
            )}
          </EditableSection>

          <EditableSection
            title={'Instructions'}
            flex={true}
            isEditable={canAmend('Non_Requotable') && editMode}
            onEdit={() => {
              toggleEdit('general')
            }}
            editing={editStates.general}
            showDirtyBanner={
              editedStates.includes('flightDetails') ||
              editedStates.includes('specialInstructions')
            }
            showClose={false}
            isQuoteRefetching={isQuoteRefetching}
            isAnyEditing={isAnyEditing}
          >
            <InstructionsForm
              editStates={editStates}
              preparedQuote={preparedQuote}
              preparedAmend={preparedAmend}
              updatePreparedAmend={updatePreparedAmend}
              updatePreparedQuote={updatePreparedQuote}
            />
            {editStates.general && (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  gap: '10px',
                  mt: 2,
                }}
              >
                <Button
                  variant="outlined"
                  onClick={() => {
                    toggleEdit('general')
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  onClick={() => {
                    if (
                      preparedQuote?.outbound?.flightDetails?.flightNumber !==
                      currentBooking.booking.flightDetails.flightNumber
                    ) {
                      pushEditedState('flightDetails')
                      updatePreparedAmend(
                        'flightDetails',
                        preparedQuote?.outbound?.flightDetails,
                      )
                    }
                    if (
                      preparedQuote?.outbound?.specialInstructions !==
                      currentBooking.booking.specialInstructions
                    ) {
                      pushEditedState('specialInstructions')
                      updatePreparedAmend(
                        'specialInstructions',
                        preparedQuote?.outbound?.specialInstructions,
                      )
                    }
                    setEditState('general', false)
                  }}
                >
                  Apply Changes
                </Button>
              </Box>
            )}
          </EditableSection>
        </Box>
      </Box>
    </Grid>
  )
}

const AmendModal = ({
  openViewEditModal,
  setOpenViewEditModal,
  selectedBookingId,
  setBookingId,
}: ViewAmendModalProps) => {
  const [originalValues, setOriginalValues] = useState<any>({})

  const [editMode, setEditMode] = useState<boolean>(false)
  const [preparedAmend, setPreparedAmend] = useState<any>()
  const [preparedQuote, setPreparedQuote] = useState<any>()
  const [quotedJobs, setQuotedJobs] = useState<any>()
  const [pickup, setPickup] = useState<any>()
  const [dropOff, setDropOff] = useState<any>()
  const [additionalStops, setAdditionalStops] = useState<any>([])
  const [chosenVehicle, setChosenVehicle] = useState<any>(null)
  const [editStates, setEditStates] = useState<EditStates>(INITIAL_EDIT_STATES)
  const [editedStates, setEditedStates] = useState<string[]>([])
  const editStatesRef = useRef(editStates)
  const [numPassengers, setNumPassengers] = useState<number>(0)
  const preparedQuoteRef = useRef<any>(null)
  const isAnyEditing = Object.values(editStates).some((v) => v === true)
  const { validateAccess } = useUserRole()

  useEffect(() => {
    preparedQuoteRef.current = preparedQuote
  }, [preparedQuote])

  const pushEditedState = useCallback((key: string) => {
    setEditedStates((prev) => (prev.includes(key) ? prev : [...prev, key]))
  }, [])
  const removedEditedState = useCallback((key: string) => {
    setEditedStates((prev) => (prev.includes(key) ? prev : [...prev, key]))
  }, [])

  useEffect(() => {
    editStatesRef.current = editStates
  }, [editStates])

  useEffect(() => {
    if (typeof preparedAmend !== 'undefined') {
      setEditedStates((prev) =>
        Array.from(new Set([...(prev ?? []), ...Object.keys(preparedAmend)])),
      )
    }
  }, [preparedAmend])

  useEffect(() => {
    if (chosenVehicle) {
      pushEditedState('changedVehicle')
    }
  }, [chosenVehicle, pushEditedState])

  useEffect(() => {
    if (!openViewEditModal) {
      if (setBookingId) setBookingId(0)
      setEditStates(INITIAL_EDIT_STATES)
    }
  }, [openViewEditModal])

  const updatePreparedAmend = (key: any, value: any) => {
    setPreparedAmend((prev: any) => {
      if (value === undefined) {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars, no-unused-vars
        const { [key]: _, ...rest } = prev || {}

        return Object.keys(rest).length > 0 ? rest : undefined
      }

      return {
        ...prev,
        [key]: value,
      }
    })
  }

  const updatePreparedQuote = useCallback(
    (key: keyof TaxiQuote, value: any) => {
      setPreparedQuote((prev: any) => {
        const next = { ...(prev ?? {}), [key]: value } as TaxiQuote
        return next
      })
    },
    [],
  )
  useEffect(() => {
    console.log('updatePreparedAmend', preparedAmend)
  }, [preparedAmend])

  useEffect(() => {
    console.log('updatePreparedQuote', preparedQuote)
  }, [preparedQuote])

  const stopsKey = (stops: any[] = []) =>
    stops
      .map((s) => `${s.latitude}:${s.longitude}:${s.formattedAddress ?? ''}`)
      .join('|')

  useEffect(() => {
    if (!preparedQuote?.outbound) return

    const nextOutbound = {
      ...preparedQuote.outbound,
      ...(pickup !== undefined && { pickup }),
      ...(dropOff !== undefined && { dropOff }),
      ...(editStates.journey && {
        additionalStops: Array.isArray(additionalStops) ? additionalStops : [],
      }),
    }

    if (!_.isEqual(nextOutbound, preparedQuote.outbound)) {
      updatePreparedQuote('outbound', nextOutbound)
    }
  }, [pickup, dropOff, additionalStops, editStates.journey])

  useEffect(() => {
    if (!preparedQuote?.outbound) return

    const o = preparedQuote.outbound

    if (!_.isEqual(pickup, o.pickup)) setPickup(o.pickup)
    if (!_.isEqual(dropOff, o.dropOff)) setDropOff(o.dropOff)

    const outboundStops = o.additionalStops ?? []
    if (stopsKey(additionalStops) !== stopsKey(outboundStops)) {
      setAdditionalStops(outboundStops)
    }
  }, [preparedQuote?.outbound])

  function updateStops(
    index: number,
    newStop: (typeof additionalStops)[number],
  ) {
    setAdditionalStops((prev: any[]) => {
      const base = Array.isArray(prev) ? prev : []
      if (index < 0 || index >= base.length) return base
      const copy = [...base]
      copy[index] = { ...copy[index], ...newStop }
      return copy
    })
  }

  const storeOriginalValues = useCallback((key: string, values: any) => {
    setOriginalValues((prev: any) => ({ ...prev, [key]: values }))
  }, [])

  const revertToOriginal = useCallback(
    (key: string) => {
      const original = originalValues[key]
      if (!original) return

      switch (key) {
        case 'journey':
          setPickup(original.pickup)
          setDropOff(original.dropOff)
          setAdditionalStops(original.additionalStops || [])
          updatePreparedQuote('outbound', {
            ...preparedQuote?.outbound,
            pickup: original.pickup,
            dropOff: original.dropOff,
            additionalStops: original.additionalStops || [],
          })
          updatePreparedAmend('amendedQuote', undefined)
          setEditedStates((prev) =>
            prev.filter((item) => item !== 'changedJourney'),
          )
          break
        case 'date':
          updatePreparedQuote('outbound', {
            ...preparedQuote?.outbound,
            when: original.when,
          })
          updatePreparedAmend('amendedQuote', undefined)
          setEditedStates((prev) =>
            prev.filter((item) => item !== 'changedDate'),
          )
          break
        case 'passengers':
          setNumPassengers(original.numPassengers)
          updatePreparedQuote('numberOfPassengers', original.numberOfPassengers)
          updatePreparedQuote('passengers', original.passengers)
          updatePreparedAmend('numberOfPassengers', undefined)
          updatePreparedAmend('passengers', undefined)
          setEditedStates((prev) =>
            prev.filter(
              (item) => item !== 'numberOfPassengers' && item !== 'passengers',
            ),
          )
          break
        case 'general':
          updatePreparedQuote('outbound', {
            ...preparedQuote?.outbound,
            specialInstructions: original.specialInstructions,
            flightDetails: original.flightDetails,
          })
          updatePreparedAmend('specialInstructions', undefined)
          updatePreparedAmend('flightDetails', undefined)
          setEditedStates((prev) =>
            prev.filter(
              (item) =>
                item !== 'flightDetails' && item !== 'specialInstructions',
            ),
          )
          break
        case 'bookingRefs':
          updatePreparedQuote('bookingReferences', original.bookingReferences)
          updatePreparedAmend('bookingReferences', undefined)
          setEditedStates((prev) =>
            prev.filter((item) => item !== 'bookingReferences'),
          )
          break
        case 'vehicle':
          setChosenVehicle(original.chosenVehicle)
          updatePreparedAmend('amendedQuote', undefined)
          setEditedStates((prev) =>
            prev.filter((item) => item !== 'changedVehicle'),
          )
          break
      }
    },
    [originalValues, preparedQuote, updatePreparedQuote, updatePreparedAmend],
  )

  const toggleEdit = useCallback(
    (key: keyof typeof editStates) => {
      setEditStates((prev) => {
        const newEditState = !prev[key]

        if (newEditState) {
          switch (key) {
            case 'journey':
              storeOriginalValues(key, {
                pickup,
                dropOff,
                additionalStops: [...(additionalStops || [])],
              })
              break
            case 'date':
              storeOriginalValues(key, {
                when: preparedQuote?.outbound?.when,
              })
              break
            case 'passengers':
              storeOriginalValues(key, {
                numPassengers,
                numberOfPassengers: preparedQuote?.numberOfPassengers,
                passengers: preparedQuote?.passengers,
              })
              break
            case 'general':
              storeOriginalValues(key, {
                specialInstructions:
                  preparedQuote?.outbound?.specialInstructions,
                flightDetails: preparedQuote?.outbound?.flightDetails,
              })
              break
            case 'bookingRefs':
              storeOriginalValues(key, {
                bookingReferences: preparedQuote?.bookingReferences,
              })
              break
            case 'vehicle':
              storeOriginalValues(key, {
                chosenVehicle,
              })
              break
          }
        } else {
          revertToOriginal(key)
        }

        return { ...prev, [key]: newEditState }
      })
    },
    [
      pickup,
      dropOff,
      additionalStops,
      numPassengers,
      preparedQuote,
      chosenVehicle,
      storeOriginalValues,
      revertToOriginal,
    ],
  )

  const setEditState = useCallback(
    (key: keyof typeof editStates, value: boolean) => {
      setEditStates((prev) => ({ ...prev, [key]: value }))
    },
    [],
  )

  const {
    data: currentBooking,
    refetch: currentBookingFetch,
    isLoading: currentBookingLoading,
    isSuccess: currentBookingSuccess,
  }: any = useQuery({
    queryKey: ['getAllBookingData', selectedBookingId],
    queryFn: async () => {
      if (selectedBookingId && selectedBookingId > 0) {
        try {
          const [bookingResponse, jobsResponse] = await Promise.all([
            bookingsAPI.getBooking(selectedBookingId),
            bookingsAPI.getBookingJobs(selectedBookingId),
          ])

          if (
            bookingResponse &&
            (bookingResponse.parentBookingId ||
              bookingResponse.associatedBookings) &&
            bookingResponse.journeyTypeName === 'Return'
          ) {
            bookingResponse.relatedBooking = await bookingsAPI.getBooking(
              bookingResponse.parentBookingId ??
                bookingResponse.associatedBookings,
            )
          }

          const [client, bookingRefsResponse] = await Promise.all([
            clientsAPI.getClient(bookingResponse.client.clientId),
            clientsAPI.getBookingReferences(bookingResponse.client.clientId),
          ])

          bookingResponse.client.displayVAT =
            client.applicationTheme.displayVATPrice

          const jobWithPolicy = await Promise.all(
            jobsResponse.value.map(async (job: any) => {
              try {
                const policyStatus = await bookingsAPI.getPolicyStatus(
                  job.bookingReference,
                  job.journeyReference,
                )
                return {
                  ...job,
                  policy: policyStatus.data,
                }
              } catch (err: any) {
                if (err?.response?.status === 403) {
                  const lockedPolicy = {
                    isCancellable: false,
                    cancellationCharge: 0,
                    amendableProperties: [
                      {
                        changeType: 'Non_Requotable',
                        isAmendable: false,
                        unamendableReason:
                          'You are not permitted to amend this booking.',
                      },
                      {
                        changeType: 'Requotable',
                        isAmendable: false,
                        unamendableReason:
                          'You are not permitted to amend this booking.',
                      },
                    ],
                  }
                  return { ...job, policy: lockedPolicy }
                }

                throw err
              }
            }),
          )

          bookingResponse.totalBookingSeats = jobWithPolicy.reduce(
            (sum, job) => {
              return sum + (job?.vehicleDetails?.seats || 0)
            },
            0,
          )

          await jobsResponse.value.map(async (job: any) => {
            const auditBookingJob = await bookingsAPI.vehicleAudit(
              bookingResponse.id,
              job.id,
            )
            const amendments = auditBookingJob.data.value.filter(
              (update: any) => update.status === 'Booking_Update_Pending',
            )
            bookingResponse.amendedSinceBooking = amendments.length > 0
          })

          const bookRefMap = new Map(
            bookingResponse.bookingReferenceValues.map((item) => [
              item.bookingReferenceId,
              item.value,
            ]),
          )
          const mergedBookingRefs = bookingRefsResponse.data.value.map(
            (obj) => ({
              ...obj,
              value: bookRefMap.get(obj.id) ?? null,
            }),
          )

          bookingRefsResponse.data.value.forEach((ref: any) => {
            ref.value = bookingResponse.bookingReferenceValues[ref.id]
            ref.possibleValues = ref.values
            delete ref.values
          })
          bookingResponse.policy = {
            anyAmendable:
              jobWithPolicy[0]?.policy?.amendableProperties?.some(
                (p: any) => p.isAmendable,
              ) || false,
            cancellation: {
              possibe: jobWithPolicy[0]?.policy?.isCancellable,
              fee: jobWithPolicy[0]?.policy?.cancellationCharge,
            },
          }
          const returnedObj = {
            booking: bookingResponse,
            jobs: jobWithPolicy,
            policy: bookingResponse.policy,
            accountRefs: mergedBookingRefs,
          }
          console.log('returnedObj', returnedObj)
          return returnedObj
        } catch (error) {
          console.error('Error fetching booking data:', error)
          throw error
        }
      }
      return null
    },
    enabled: false,
  })

  const submitAmend = useMutation({
    mutationKey: ['updateBooking', preparedAmend?.amendedQuote?.quoteReference],
    mutationFn: async () => {
      return await bookingsAPI.updateBooking(
        currentBooking?.booking.id,
        preparedAmend,
      )
    },
    onMutate: () => {
      return { id: 1 }
    },
    onError: (error: Error) => {
      notifyPopup({
        type: 'warning',
        title: 'Unable to update your booking.',
        message: `${error.message}`,
      })
    },
    onSuccess: () => {
      const prevPriceObj = currentBooking?.jobs?.[0]?.estimatedPrice
      const newPriceObj = chosenVehicle?.estimatedTotal
      const previousPrice =
        prevPriceObj?.total ?? prevPriceObj?.price ?? undefined
      const newPrice =
        newPriceObj?.total ??
        newPriceObj?.price ??
        (chosenVehicle ? undefined : previousPrice)
      const currency =
        prevPriceObj?.currency ??
        newPriceObj?.currency ??
        currentBooking?.booking?.currency
      const dirtyKeys = preparedAmend ? Object.keys(preparedAmend) : []
      fireBookingAmended({
        bookingId: currentBooking?.booking?.id,
        bookingReference: currentBooking?.booking?.bookingReference,
        dirtyFieldKeys: dirtyKeys,
        changedVehicle: Boolean(preparedAmend?.amendedQuote),
        changedPassenger: dirtyKeys.includes('passengers'),
        changedFlight: dirtyKeys.includes('flightDetails'),
        changedInstructions: dirtyKeys.includes('specialInstructions'),
        previousPrice,
        newPrice,
        currency,
      })
      notifyPopup({
        type: 'success',
        title: 'Amendment Requested',
        message: 'Amendments to your booking have been successfully submitted.',
      })
      setOpenViewEditModal(false)
    },
  })

  function canAmend(type: any) {
    // if (!validateAccess('Jyrney_Admin')) return false --remove restriction on amend

    const entry = currentBooking?.jobs?.[0]?.policy?.amendableProperties?.find(
      (c: any) => c.changeType === type,
    )
    return Boolean(entry?.isAmendable)
  }
  const amendableProps = currentBooking?.jobs[0].policy.amendableProperties

  const nonRequotReason = amendableProps?.find(
    (p: any) => p.changeType === 'Non_Requotable',
  )?.unamendableReason
  const requotReason = amendableProps?.find(
    (p: any) => p.changeType === 'Requotable',
  )?.unamendableReason
  const uniqueReasons = Array.from(
    new Set([nonRequotReason, requotReason].filter(Boolean)),
  )

  const { data: clientUserData }: any = useQuery({
    queryKey: ['getClientUsers', selectedBookingId],
    queryFn: async () => {
      if (selectedBookingId && selectedBookingId > 0) {
        try {
          const usersResponse = await usersAPI.getUsersByClientId(
            currentBooking?.booking.client.clientId,
          )

          return usersResponse.data.value
        } catch (error) {
          console.error('Error fetching booking data:', error)
          return []
        }
      }
      return null
    },
    enabled: currentBookingSuccess,
  })
  useEffect(() => {
    if (!openViewEditModal) return
    setPreparedQuote(undefined)
    setPreparedAmend(undefined)
    setPickup(undefined)
    setDropOff(undefined)
    setAdditionalStops([])
    setChosenVehicle(undefined)
    setQuotedJobs(undefined)
    currentBookingFetch()
  }, [selectedBookingId])

  useEffect(() => {
    if (!preparedQuote?.outbound) return
    setPickup(preparedQuote.outbound.pickup)
    setDropOff(preparedQuote.outbound.dropOff)
    setAdditionalStops(preparedQuote.outbound.additionalStops ?? [])
  }, [preparedQuote?.outbound, selectedBookingId])

  const quoteMutation = useMutation({
    mutationKey: ['getQuote'],
    mutationFn: async (payload: TaxiQuote) => {
      const response = await quotesAPI.createQuote(payload)
      const outbound = response?.data?.outbound
      if (!outbound?.vehicles) {
        throw {
          msg: 'There are no vehicles currently available for the requested journey, date and time.',
        }
      }
      return outbound
    },
    onError: (err: any) => {
      confirmAlert({
        customUI: ({ onClose }) => {
          return (
            <Dialog open={true} onClose={onClose}>
              <DialogTitle>No quotes available</DialogTitle>
              <DialogContent sx={{ marginTop: '20px' }}>
                {err?.msg
                  ? err.msg
                  : ` We weren’t able to generate a quote for this trip. This can
                sometimes happen if the pickup point, any stopovers, or the
                destination falls outside of our usual coverage areas.`}
              </DialogContent>
              <DialogActions>
                <Button color="primary" onClick={onClose}>
                  Ok
                </Button>
              </DialogActions>
            </Dialog>
          )
        },
      })
    },
    onSuccess: (outbound: any) => {
      const currentVehicle = chosenVehicle ?? currentBooking?.jobs[0]
      const allVehicles = outbound.vehicles ?? []

      const matchPreviousBooking = allVehicles.filter(
        (vehicle: any) =>
          vehicle.vehicleDetails.vehicleTypeName ===
            currentVehicle?.vehicleDetails?.vehicleTypeName &&
          vehicle.supplierName === currentVehicle?.supplier?.companyName,
      )

      const shouldShowAll =
        matchPreviousBooking.length === 0 || editStatesRef.current.vehicle

      const payload = preparedQuoteRef.current
      const quotes = (shouldShowAll ? allVehicles : matchPreviousBooking)
        .map((q: any) => ({ ...q, price: q.estimatedPrice }))
        .filter(
          (q: any) =>
            q?.vehicleDetails?.seats >= (payload?.numberOfPassengers ?? 0),
        )

      const quoteRef = outbound.quoteReference
      const quoteObj = { quoteRef, quotes }

      let nextSelected: any | undefined
      if (chosenVehicle) {
        nextSelected = quotes.find(
          (q: any) =>
            q.vehicleDetails?.vehicleTypeName ===
              chosenVehicle?.vehicleDetails?.vehicleTypeName &&
            q.supplierName === chosenVehicle?.supplierName,
        )
      }

      const canAutoSelect =
        quotes.length === 1 && !editStatesRef.current.vehicle

      if (canAutoSelect) {
        const single = quotes[0]
        setQuotedJobs(single)
        setChosenVehicle(single)
        if (editStatesRef.current.vehicle) setEditState('vehicle', false)
        if (editStatesRef.current.date) setEditState('date', false)
        if (editStatesRef.current.journey) setEditState('journey', false)
        updatePreparedAmend('amendedQuote', {
          quoteReference: quoteRef,
          vehicles: [
            {
              supplierQuoteReference: single.supplierQuoteReference,
              quantity: 1,
            },
          ],
        })
        return
      }

      setQuotedJobs(quoteObj)

      if (nextSelected) {
        setChosenVehicle(nextSelected)
        updatePreparedAmend('amendedQuote', {
          quoteReference: quoteRef,
          vehicles: [
            {
              supplierQuoteReference: nextSelected.supplierQuoteReference,
              quantity: 1,
            },
          ],
        })
      } else {
        if (chosenVehicle) setChosenVehicle(undefined)
        updatePreparedAmend('amendedQuote', {
          quoteReference: quoteRef,
          vehicles: [],
        })
        if (quotes.length > 0 && !editStatesRef.current.vehicle) {
          setEditState('vehicle', true)
        }
      }
    },
  })

  const quoteRefetch = useCallback(
    (payload?: TaxiQuote) => {
      const toSend = payload ?? preparedQuoteRef.current
      if (!toSend?.outbound?.pickup || !toSend?.outbound?.dropOff) return
      return quoteMutation.mutateAsync(toSend)
    },
    [quoteMutation],
  )

  const isQuoteRefetching = quoteMutation.isPending

  const resetModalState = () => {
    if (setBookingId) setBookingId(0)
    setEditMode(false)
    setEditStates(INITIAL_EDIT_STATES)
    setOriginalValues({})
    setPickup(undefined)
    setDropOff(undefined)
    setAdditionalStops([])
    setEditedStates([])
    setPreparedAmend(undefined)
    setPreparedQuote(undefined)
    setQuotedJobs(undefined)
    setChosenVehicle(undefined)
  }

  useEffect(() => {
    if (openViewEditModal) {
      currentBookingFetch()
    } else {
      resetModalState()
    }
  }, [openViewEditModal])

  useEffect(() => {
    if (currentBooking && !currentBookingLoading && !preparedQuote) {
      setPreparedQuote({
        clientAccountNumber: currentBooking.booking.client.accountNumber,
        bookerId: currentBooking.booking.booker.id,
        numberOfPassengers: currentBooking.booking.numberOfPassengers,
        passengers: currentBooking.booking.passengers,
        bookingReferences: currentBooking.booking.bookingReferenceValues,
        journeyTypeName:
          currentBooking.booking.journeyTypeName == 'Return'
            ? 'OneWay'
            : currentBooking.booking.journeyTypeName,
        directedJourneyTime: currentBooking.booking.directedJourneyTime,
        isImmediateJourney: false,
        outbound: {
          when: currentBooking.booking.when,
          dropOff: currentBooking.booking.dropOff,
          pickup: currentBooking.booking.pickup,
          additionalStops: currentBooking.booking.additionalStops ?? [],
          specialInstructions: currentBooking.booking.specialInstructions,
          flightDetails: {
            flightNumber:
              currentBooking.booking.flightDetails.flightNumber || '',
          },
        },
        inbound: undefined,
        vehicles: [],
      })
      setNumPassengers(currentBooking.booking.numberOfPassengers - 1)
    } else {
      currentBookingFetch()
    }
  }, [currentBooking, openViewEditModal])

  return (
    <Modal
      open={openViewEditModal}
      onClose={(event, reason) => {
        if (event && typeof preparedAmend !== 'undefined') {
          if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
            confirmAlert({
              customUI: ({ onClose }) => {
                return (
                  <Dialog
                    open={true}
                    onClose={onClose}
                    aria-labelledby="alert-dialog-title"
                    aria-describedby="alert-dialog-description"
                  >
                    <DialogTitle
                      id="alert-dialog-title"
                      sx={(theme) => ({
                        backgroundColor: theme.palette.primary.main,
                        color: theme.palette.info.contrastText,
                        fontSize: 'var(--fs-20, 1.25rem)',
                        fontWeight: 600,
                      })}
                    >
                      Unsaved changes
                    </DialogTitle>
                    <DialogContent sx={{ marginTop: '20px' }}>
                      You’ve made changes to this booking. If you leave, those
                      changes will be lost.
                    </DialogContent>
                    <DialogActions>
                      <Button onClick={onClose}>Go back</Button>
                      <Button
                        variant="contained"
                        color="error"
                        onClick={async () => {
                          try {
                            onClose()
                            resetModalState()
                            setOpenViewEditModal(false)
                          } catch (err) {
                            console.log(err)
                          }
                        }}
                      >
                        Discard Changes
                      </Button>
                      <Button
                        color="success"
                        variant="contained"
                        onClick={async () => {
                          try {
                            submitAmend.mutate()
                            setOpenViewEditModal(false)
                            resetModalState()
                            onClose()
                          } catch (err) {
                            console.log(err)
                          }
                        }}
                      >
                        Update Booking
                      </Button>
                    </DialogActions>
                  </Dialog>
                )
              },
            })
          }
        } else {
          setOpenViewEditModal(false)
          resetModalState()
        }
      }}
      aria-labelledby="amendBooking"
      aria-describedby="Amend Booking Modal"
    >
      <Box
        sx={{
          position: 'absolute' as 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '95dvw',
          height: '90dvh',
          minHeight: '600px',
          maxWidth: '2100px',
          minWidth: '1200px',
          bgcolor: '#f0f0f0',
          borderRadius: '10px',
          boxShadow: 24,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {currentBooking && !currentBookingLoading ? (
          <>
            <Header
              currentBooking={currentBooking}
              setOpenViewEditModal={setOpenViewEditModal}
              editMode={editMode}
              editStates={editStates}
              setEditMode={setEditMode}
              preparedAmend={preparedAmend}
              submitAmend={submitAmend}
            />
            <Box sx={{ overflowY: 'auto', flexGrow: 1 }}>
              <Slide
                direction="down"
                in={typeof preparedAmend !== 'undefined'}
                mountOnEnter
                unmountOnExit
              >
                <Box sx={{ width: '100%', pt: 1, px: 1 }}>
                  <Alert severity="warning">
                    You have made changes to this booking that haven&apos;t yet
                    been saved. To make sure those changes are requested, please
                    click &apos;Update Booking&apos;.
                  </Alert>
                </Box>
              </Slide>

              <MainSection
                quoteRefetch={quoteRefetch}
                isQuoteRefetching={isQuoteRefetching}
                canAmend={canAmend}
                editMode={editMode}
                editStates={editStates}
                toggleEdit={toggleEdit}
                dropOff={dropOff}
                pickup={pickup}
                additionalStops={additionalStops}
                setDropOff={setDropOff}
                setPickup={setPickup}
                setAdditionalStops={setAdditionalStops}
                preparedQuote={preparedQuote}
                pushEditedState={pushEditedState}
                currentBooking={currentBooking}
                updatePreparedQuote={updatePreparedQuote}
                setEditState={setEditState}
                updatePreparedAmend={updatePreparedAmend}
                chosenVehicle={chosenVehicle}
                setChosenVehicle={setChosenVehicle}
                quotedJobs={quotedJobs}
                numPassengers={numPassengers}
                setNumPassengers={setNumPassengers}
                clientUserData={clientUserData}
                uniqueReasons={uniqueReasons}
                editedStates={editedStates}
                removedEditedState={removedEditedState}
                preparedAmend={preparedAmend}
                updateStops={updateStops}
                isAnyEditing={isAnyEditing}
                validateAccess={validateAccess}
              />
            </Box>
          </>
        ) : (
          <Box
            sx={{
              display: 'flex',
              flexGrow: 1,
              height: '100%',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <CircularProgress />
          </Box>
        )}
        <QuoteProgressOverlay open={isQuoteRefetching} />
      </Box>
    </Modal>
  )
}

export default AmendModal
